// Local tools (§11.4, §11.8, §11.10, §11.11): new-mask defaults, stroke building and the heal source
// auto-pick. Pure functions (no DOM, no GL) so they are unit-tested on arrays.
import type { HealSpot, LocalMask, MaskAdj, Stroke, StrokePoint } from '../model/types';

export const MAX_MASKS = 8;
export const MAX_HEAL = 200;

export const zeroAdj = (): MaskAdj => ({
  exposure: 0,
  contrast: 0,
  temp: 0,
  tint: 0,
  saturation: 0,
  highlights: 0,
  shadows: 0,
});

let serial = 0;
const newId = () => `m${Date.now().toString(36)}${(serial++).toString(36)}`;

/** A new mask with sensible starting geometry (centre of the photo). */
export function newMask(kind: LocalMask['kind']): LocalMask {
  const geom: LocalMask['geom'] =
    kind === 'linear'
      ? { x0: 0.5, y0: 0.25, x1: 0.5, y1: 0.6 }
      : kind === 'radial'
        ? { cx: 0.5, cy: 0.5, rx: 0.25, ry: 0.25, angle: 0, feather: 0.5 }
        : { strokes: [] };
  return { id: newId(), kind, geom, invert: false, adj: zeroAdj() };
}

export type BrushOptions = { size: number; feather: number; flow: number };

export function newStroke(p: StrokePoint, o: BrushOptions, erase: boolean): Stroke {
  return { points: [p], size: o.size, feather: o.feather, flow: o.flow, erase };
}

/**
 * Appends a point when it has moved at least a quarter of the brush radius (in source pixels), so long
 * strokes stay small. Returns true when the point was added.
 */
export function extendStroke(s: Stroke, p: StrokePoint, W: number, H: number): boolean {
  const last = s.points[s.points.length - 1]!;
  const r = (s.size / 2) * Math.min(W, H);
  if (Math.hypot((p.x - last.x) * W, (p.y - last.y) * H) < Math.max(1, r / 4)) return false;
  s.points.push(p);
  return true;
}

/**
 * §11.8 default heal source: among 8 candidate offsets at distance 2·radius, the one whose disc has the
 * lowest gradient energy (non-AI). `luma(x, y)` reads luminance at source pixel coordinates; candidates
 * whose disc leaves the image are skipped. Ties keep the first candidate, (+2r, 0). Offsets are returned
 * source-normalized.
 */
export function autoHealOffset(
  luma: (x: number, y: number) => number,
  cx: number,
  cy: number,
  r: number,
  W: number,
  H: number,
): { x: number; y: number } {
  let best = { x: (2 * r) / W, y: 0 };
  let bestScore = Infinity;
  const n = 7;
  for (let k = 0; k < 8; k++) {
    const a = (k * Math.PI) / 4;
    const ox = Math.cos(a) * 2 * r,
      oy = Math.sin(a) * 2 * r;
    const sx = cx + ox,
      sy = cy + oy;
    if (sx - r < 0 || sy - r < 0 || sx + r > W - 1 || sy + r > H - 1) continue;
    let e = 0;
    for (let j = 0; j < n; j++)
      for (let i = 0; i < n; i++) {
        const x = sx + ((i / (n - 1)) * 2 - 1) * r,
          y = sy + ((j / (n - 1)) * 2 - 1) * r;
        if (Math.hypot(x - sx, y - sy) > r) continue;
        const gx = luma(x + 1, y) - luma(x - 1, y),
          gy = luma(x, y + 1) - luma(x, y - 1);
        e += Math.hypot(gx, gy);
      }
    if (e < bestScore - 1e-9) {
      bestScore = e;
      best = { x: ox / W, y: oy / H };
    }
  }
  return best;
}

export function newHealSpot(
  stroke: Stroke,
  offset: { x: number; y: number },
  kind: HealSpot['kind'],
  opacity = 1,
): HealSpot {
  return { kind, stroke, offset, opacity };
}

/** Centre of a stroke (mean of its points), source-normalized. */
export function strokeCentre(s: Stroke): { x: number; y: number } {
  let x = 0,
    y = 0;
  for (const p of s.points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / s.points.length, y: y / s.points.length };
}
